import React from "react";
import {
  GlobeAltIcon,
  UsersIcon,
  ShieldCheckIcon
} from "@heroicons/react/24/outline";

import PageHeader from "../components/PageHeader";
import aboutImg from "../assets/images/aboutUs.jpg";
import { Helmet } from "react-helmet-async";
import {
  FaHotel,
  FaPlane,
  FaTaxi,
  FaCar,
  FaShip,
  FaUmbrellaBeach,
  FaPassport,
  FaMapMarkedAlt,
} from "react-icons/fa";

export default function AboutUs() {
  return (
    <>
      <Helmet>
        {/* Basic SEO */}
        <title>About Us | UAE Discounted Hotels</title>

        <meta
          name="description"
          content="Learn more about UAE Discounted Hotels. We help travelers find the best discounted hotels, exclusive hotel deals, and affordable accommodation across Dubai, Abu Dhabi, Sharjah, and the UAE."
        />

        <meta
          name="keywords"
          content="About UAE Discounted Hotels, UAE hotel deals, discounted hotels UAE, Dubai hotels, Abu Dhabi hotels, Sharjah hotels, affordable hotels UAE, hotel booking UAE"
        />

        <meta name="robots" content="index, follow" />
        <meta name="author" content="UAE Discounted Hotels" />

        {/* Canonical */}
        <link
          rel="canonical"
          href="https://uaediscountedhotels.com/about-us"
        />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="UAE Discounted Hotels" />
        <meta property="og:locale" content="en_US" />

        <meta
          property="og:title"
          content="About Us | UAE Discounted Hotels"
        />

        <meta
          property="og:description"
          content="Discover the story behind UAE Discounted Hotels and our mission to provide the best hotel deals and affordable stays across the UAE."
        />

        <meta
          property="og:url"
          content="https://uaediscountedhotels.com/about-us"
        />

        <meta
          property="og:image"
          content="https://uaediscountedhotels.com/images/default-og.png"
        />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />

        <meta
          name="twitter:title"
          content="About Us | UAE Discounted Hotels"
        />

        <meta
          name="twitter:description"
          content="Learn more about UAE Discounted Hotels and how we help travelers discover the best hotel deals across the UAE."
        />

        <meta
          name="twitter:image"
          content="https://uaediscountedhotels.com/images/default-og.png"
        />
      </Helmet>

      <div className="w-full bg-gray-50 dark:bg-gray-950 transition-colors duration-300">

        {/* HEADER */}
        <PageHeader
          title="About South Travels"
          subtitle="Customer satisfaction is the success of our business"
        />

        {/* INTRO SECTION */}
        <section className="container mx-auto px-6 py-14 sm:py-20 grid grid-cols-1 md:grid-cols-2 gap-10 items-center">

          {/* TEXT */}
          <div className="space-y-5">

            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white leading-snug">
              Leading Online Travel Agency in UAE
            </h2>

            <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
              South Travels is one of the most reputable online travel agencies in Dubai,
              with more than a decade of experience in providing quality travel services
              for leisure and business travelers worldwide.
            </p>

            <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
              We are committed to integrity, customer satisfaction, and delivering
              professional travel experiences across the globe.
            </p>

            {/* STATS */}
            <div className="flex flex-wrap gap-6 pt-2">

              <div className="min-w-[120px]">
                <p className="text-blue-600 dark:text-cyan-400 font-bold text-xl">25+</p>
                <p className="text-sm text-gray-600 dark:text-gray-400">Years Experience</p>
              </div>

              <div className="min-w-[120px]">
                <p className="text-blue-600 dark:text-cyan-400 font-bold text-xl">1000+</p>
                <p className="text-sm text-gray-600 dark:text-gray-400">Happy Clients</p>
              </div>

              <div className="min-w-[120px]">
                <p className="text-blue-600 dark:text-cyan-400 font-bold text-xl">24/7</p>
                <p className="text-sm text-gray-600 dark:text-gray-400">Support</p>
              </div>

            </div>

          </div>

          {/* IMAGE */}
          <div className="w-full">
            <img
              src={aboutImg}
              alt="South Travels"
              className="w-full h-[280px] sm:h-[350px] md:h-[420px] object-cover rounded-2xl shadow-lg"
            />
          </div>

        </section>

        {/* SERVICES SECTION */}
        <section className="bg-white dark:bg-gray-900 py-14 sm:py-20">
          <div className="container mx-auto px-6">

            <div className="text-center mb-14">
              <span className="inline-block px-4 py-1 rounded-full bg-blue-100 dark:bg-cyan-500/10 text-blue-600 dark:text-cyan-400 text-sm font-medium mb-4">
                What We Offer
              </span>

              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white">
                Our Services
              </h2>

              <p className="mt-4 max-w-2xl mx-auto text-gray-600 dark:text-gray-400">
                We provide complete travel solutions for individuals, families,
                corporate clients, and groups around the world.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                {
                  title: "Hotel Booking",
                  icon: <FaHotel />,
                  link: "#",
                },
                {
                  title: "Flight Booking",
                  icon: <FaPlane />,
                  link: "#",
                },
                {
                  title: "Airport Transfers",
                  icon: <FaTaxi />,
                  link: "#",
                },
                {
                  title: "Car Rentals",
                  icon: <FaCar />,
                  link: "#",
                },
                {
                  title: "Cruise Booking",
                  icon: <FaShip />,
                  link: "#",
                },
                {
                  title: "Holiday Packages",
                  icon: <FaUmbrellaBeach />,
                  link: "#",
                },
                {
                  title: "Visa Assistance",
                  icon: <FaPassport />,
                  link: "#",
                },
                {
                  title: "Tours & Activities",
                  icon: <FaMapMarkedAlt />,
                  link: "#",
                },
              ].map((service, i) => (
                <a
                  key={i}
                  href={service.link}
                  className="group block bg-gray-50 dark:bg-gray-950 border border-gray-200 dark:border-gray-800 rounded-2xl p-6 text-center hover:-translate-y-2 hover:shadow-xl transition-all duration-300"
                >
                  <div className="w-14 h-14 mx-auto mb-5 rounded-2xl bg-gradient-to-r from-blue-600 to-cyan-500 text-white flex items-center justify-center text-xl shadow-lg group-hover:scale-110 transition-transform duration-300">
                    {service.icon}
                  </div>

                  <h3 className="font-semibold text-gray-900 dark:text-white text-base sm:text-lg">
                    {service.title}
                  </h3>

                  <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                    Professional and reliable travel service tailored to your needs.
                  </p>

                  <span className="inline-flex items-center mt-4 text-blue-600 dark:text-cyan-400 font-medium text-sm group-hover:translate-x-1 transition-transform">
                    Learn More →
                  </span>
                </a>
              ))}
            </div>

          </div>
        </section>

        {/* STRENGTH / VISION / OBJECTIVE */}
        <section className="container mx-auto px-6 py-14 sm:py-20">

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">

            {/* STRENGTH */}
            <div className="bg-gradient-to-r from-blue-600 via-cyan-500 to-blue-700 text-white p-6 sm:p-7 rounded-2xl shadow-md">
              <UsersIcon className="w-10 h-10 mb-4" />
              <h3 className="font-bold text-lg mb-2">Our Strength</h3>
              <p className="text-sm text-white/80 leading-relaxed">
                Our team is our biggest strength, driven by talent and commitment.
              </p>
            </div>

            {/* VISION */}
            <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 p-6 sm:p-7 rounded-2xl shadow-sm">
              <GlobeAltIcon className="w-10 h-10 mb-4 text-blue-600 dark:text-cyan-400" />
              <h3 className="font-bold text-lg text-gray-900 dark:text-white mb-2">
                Our Vision
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
                To become a leading provider of travel and tourism services worldwide.
              </p>
            </div>

            {/* OBJECTIVE */}
            <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 p-6 sm:p-7 rounded-2xl shadow-sm">
              <ShieldCheckIcon className="w-10 h-10 mb-4 text-blue-600 dark:text-cyan-400" />
              <h3 className="font-bold text-lg text-gray-900 dark:text-white mb-2">
                Our Objective
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
                Deliver quality services, growth, and customer satisfaction.
              </p>
            </div>

          </div>

        </section>

        {/* HISTORY TIMELINE */}
        <section className="bg-gray-100 dark:bg-gray-900 py-14 sm:py-20">
          <div className="container mx-auto px-6">

            <h2 className="text-2xl sm:text-3xl font-bold text-center mb-14 text-gray-900 dark:text-white">
              Our History
            </h2>

            <div className="relative">

              {/* Horizontal line ONLY for md+ */}
              <div className="hidden md:block absolute top-5 left-0 w-full h-[2px] bg-blue-600 dark:bg-cyan-500"></div>

              {/* MOBILE VERTICAL LINE */}
              <div className="md:hidden absolute left-3 top-0 bottom-0 w-[2px] bg-blue-600 dark:bg-cyan-500"></div>

              <div className="
                flex flex-col gap-10
                md:grid md:grid-cols-4 lg:grid-cols-7
                relative
            ">

                {[
                  { year: "2000", text: "Company founded in UAE" },
                  { year: "2003", text: "SouthTravels.com launched" },
                  { year: "2007", text: "Expanded to Dubai" },
                  { year: "2010", text: "IATA Accredited Agent" },
                  { year: "2012", text: "International expansion" },
                  { year: "2014", text: "Advanced booking system launched" },
                  { year: "Today", text: "Leading travel agency in UAE" }
                ].map((item, i) => (
                  <div
                    key={i}
                    className="
                            flex md:flex-col
                            items-start md:items-center
                            text-left md:text-center
                            gap-4 md:gap-0
                            pl-10 md:pl-0
                            relative
                        "
                  >

                    {/* Dot */}
                    <div className="
                            absolute md:relative
                            left-0 md:left-auto
                            top-2 md:top-auto
                            w-4 h-4 rounded-full
                            bg-blue-600 dark:bg-cyan-400
                            border-4 border-white dark:border-gray-900
                        "></div>

                    {/* Content */}
                    <div>
                      <h3 className="font-bold text-blue-600 dark:text-cyan-400">
                        {item.year}
                      </h3>

                      <p className="mt-1 text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
                        {item.text}
                      </p>
                    </div>

                  </div>
                ))}

              </div>

            </div>

          </div>
        </section>

      </div>
    </>
  );
}